const KEY = 'peoplevue_excluded_users';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Stashed by syncExcludedUsers so exclude/restore can write through to the
// server (the endpoint is admin-only; non-admin pushes are rejected there).
let apiToken = null;

/**
 * Exclusions are stored as BOTH ids and normalized display names. The name
 * rule is what keeps removed people out for good: ghost/test accounts (and
 * rehires of deleted accounts) come back from AD sync with a brand-new
 * object id, so an id-only list would let them reappear on every sync.
 */

function normalizeName(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// "Jennifer Anne Brown" also matches a rule stored as "jennifer brown".
function firstLastName(name) {
  const parts = normalizeName(name).split(' ').filter(Boolean);
  if (parts.length <= 2) return parts.join(' ');
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function readLocal() {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY));
    if (!stored) return { ids: [], names: [] };
    // Migration: the previous shape was a plain array of ids.
    if (Array.isArray(stored)) return { ids: stored, names: [] };
    return { ids: stored.ids || [], names: stored.names || [] };
  } catch {
    return { ids: [], names: [] };
  }
}

function writeLocal(data) {
  try { localStorage.setItem(KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

/**
 * Hydrate the local exclusion rules from the server. Call once after sign-in.
 * Non-fatal on failure — falls back to the local cache.
 */
export async function syncExcludedUsers(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/excluded-users`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Excluded users API ${res.status}`);
    const data = await res.json();
    writeLocal({ ids: data?.ids || [], names: data?.names || [] });
    return true;
  } catch (err) {
    console.warn('Failed to sync excluded users (non-fatal):', err.message);
    return false;
  }
}

function pushExcluded(data) {
  if (!API_BASE || !apiToken) return;
  fetch(`${API_BASE}/excluded-users`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
    body: JSON.stringify({ ids: data.ids, names: data.names }),
  })
    .then((res) => { if (!res.ok) console.warn(`Excluded users push failed: ${res.status}`); })
    .catch((err) => console.warn('Excluded users push failed:', err.message));
}

/** Raw rules — { ids: string[], names: string[] (normalized) }. */
export function getExclusionRules() {
  return readLocal();
}

/** Back-compat: the excluded id list. */
export function getExcludedUsers() {
  return readLocal().ids;
}

/**
 * Should this user be hidden from the directory? Matches by object id OR by
 * normalized display name (with a first+last fallback so middle names don't
 * defeat the rule). The name match is what survives AD re-syncs that hand
 * the same person a new object id.
 */
export function isExcludedUser(user) {
  if (!user) return false;
  const rules = readLocal();
  if (user.id && rules.ids.includes(user.id)) return true;
  if (rules.names.length === 0 || !user.displayName) return false;
  const full = normalizeName(user.displayName);
  const firstLast = firstLastName(user.displayName);
  return rules.names.includes(full) || rules.names.includes(firstLast);
}

/**
 * Exclude a user — records the id AND the display name so the rule holds
 * even if the account is re-synced under a new id.
 */
export function excludeUser(userId, displayName = null) {
  const rules = readLocal();
  const next = {
    ids: rules.ids.includes(userId) ? rules.ids : [...rules.ids, userId],
    names: rules.names,
  };
  const name = normalizeName(displayName);
  if (name && !next.names.includes(name)) {
    next.names = [...next.names, name];
  }
  writeLocal(next);
  pushExcluded(next);
}

/** Restore a user — removes the id rule and any name rule that matches. */
export function restoreUser(userId, displayName = null) {
  const rules = readLocal();
  const full = normalizeName(displayName);
  const firstLast = firstLastName(displayName);
  const next = {
    ids: rules.ids.filter((id) => id !== userId),
    names: displayName
      ? rules.names.filter((n) => n !== full && n !== firstLast)
      : rules.names,
  };
  writeLocal(next);
  pushExcluded(next);
}

/**
 * Remove ONE exclusion rule — an id or a name, not a person.
 *
 * `restoreUser` needs both halves of the pair, which the UI cannot always
 * supply: an excluded person is not in the directory any more, so there is
 * nothing to look their id up from. The two rule lists cannot be joined back
 * together either — a name rule carries no id and vice versa. So the audit
 * list works rule by rule, and the UI has to say that a person stays hidden
 * while ANY rule still matches them.
 *
 * @param {{id?: string, name?: string}} rule Exactly one of id or name.
 * @returns {boolean} true if a rule was actually removed.
 */
export function removeExclusionRule({ id = null, name = null } = {}) {
  const rules = readLocal();
  let next = rules;
  if (id) {
    if (!rules.ids.includes(id)) return false;
    next = { ...rules, ids: rules.ids.filter((x) => x !== id) };
  } else if (name) {
    const target = normalizeName(name);
    if (!rules.names.includes(target)) return false;
    next = { ...rules, names: rules.names.filter((n) => n !== target) };
  } else {
    return false;
  }
  writeLocal(next);
  pushExcluded(next);
  return true;
}

export function isExcluded(userId) {
  return readLocal().ids.includes(userId);
}
